import * as React from "react";
import type { BrandPlan } from "../../utils/plans";
import toast from "react-hot-toast";
import { Dialog, DialogContent, Box, Typography } from "@mui/material";
import axiosInstant from "../../api";
import { IconCross } from "../../utils/icons";
import { handleConflictError } from "../../utils/errorHandler";
import BrandForm, { SaveChangesParams, BrandFormHiddenField } from "./BrandForm";
import { Brand } from "../videoProduction/components/constants";

interface Props {
  /** When provided, the form opens in edit mode against this brand. */
  brand?: any;
  onClose: () => void;
  /** Fired with the created/updated brand (the raw `/brands/create` response body). */
  onComplete: (brand: Brand) => void;
  /** Pre-fills the Name field — e.g. the text typed in the upstream autocomplete. */
  initialBrandName?: string;
  /** Initial plan for a brand created from this context (internal-only field). */
  defaultPlan?: BrandPlan;
  /** Fields to suppress so the inline-create form stays minimal. */
  hiddenFields?: BrandFormHiddenField[];
}

/**
 * Thin Dialog wrapper around BrandForm — the brand analog of ProductV2Modal.
 * It owns the single POST /brands/create and hands the created brand back via
 * onComplete, so callers no longer create brands themselves.
 */
export function BrandV2Modal({
  brand,
  onClose,
  onComplete,
  initialBrandName,
  defaultPlan,
  hiddenFields,
}: Props) {
  const handleSaveChanges: SaveChangesParams = async (payload) => {
    try {
      const { data } = await axiosInstant.post("/brands/create", payload);
      if (data?.id) {
        toast.success("Brand created successfully!");
        onComplete(data);
      } else {
        toast.error("Failed to create brand.");
      }
    } catch (error: any) {
      if (!handleConflictError(error)) {
        toast.error("Failed to create brand. Please try again.");
      }
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogContent className="!p-0 bg-surface">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          pt={3}
          pb={1}
        >
          <Typography variant="h5">{brand ? "Brand Settings" : "Add Brand"}</Typography>
          <div
            onClick={onClose}
            className="cursor-pointer hover:bg-surface-2 rounded-full p-1 text-text-1"
          >
            <IconCross height={18} width={18} stroke="currentColor" />
          </div>
        </Box>
        <BrandForm
          brandData={brand}
          saveChanges={handleSaveChanges}
          isDialog
          initialBrandName={initialBrandName}
          defaultPlan={defaultPlan}
          hiddenFields={hiddenFields}
        />
      </DialogContent>
    </Dialog>
  );
}

export default BrandV2Modal;
